import useChangeCompany from '@/hooks/useChangeCompany.js';
import { action } from '@/lib/api.js';
import sendToast from '@/lib/sendToast.js';
import setQuery from '@/lib/setQuery.js';
import { postCompany, processBundle, processInvite } from '@/redux/actions/settingsActions.js';
import setPref from '@/redux/actions/uiActions/setPref.js';
import { getOrgState, getUserState } from '@/redux/actions/userActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { query } from '@/redux/selectors/querySelectors.js';
import { isLoading, pref, signupIsComplete } from '@/redux/selectors/uiSelectors.js';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import removePref from './redux/actions/uiActions/removePref.js';
import { activeCompanyId } from './redux/selectors/userSelectors.js';
import useLoadUser from './hooks/useLoadUser.js';
import selectors from './redux/selectors/selectors.js';
import memoizeSelector from './redux/lib/memoizeSelector.js';
import { joinCompanyFromDiscovery } from './redux/actions/gravityActions.js';

const MESSAGE = 'Please wait while we create your organization...';

const PostSignUpHandler = () => {
  const { user } = useUser();
  const { pathname } = useLocation();
  const { isLoggedIn } = useLoadUser();
  const changeCompany = useChangeCompany();
  const [hasRan, setHasRan] = useState(false);
  const [hasProcessedInvite, setHasProcessedInvite] = useState(false);

  const isSignUpComplete = useSelector(signupIsComplete);
  const queryInviteId = useArgSelector(query, 'inviteId');
  const queryBundleId = useArgSelector(query, 'bundleId');
  const queryTempCompanyId = useArgSelector(query, 'tempCompanyId');
  const queryLoadBundle = useArgSelector(query, 'loadBundle') === 'true';
  const userCompanies = useSelector(memoizeSelector('userCompanies', selectors.userCompanies));

  const bundle = useArgSelector(pref, 'bundle');
  const inviteId = useArgSelector(pref, 'inviteId');
  const companyId = useArgSelector(activeCompanyId);

  const tempCompanyId = useArgSelector(pref, 'tempCompanyId');
  const isLoadingBundle = useArgSelector(isLoading, 'processBundle');
  const isProcessingCallback = pathname.includes('callback');

  const finishSignUp = () => {
    action('SET_TRANSITION_SIGN_UP', false);
    localStorage.removeItem('organization_name');
    localStorage.removeItem('achievement');
    localStorage.removeItem('organization_domain');
    localStorage.removeItem('allow_discovery');
    localStorage.removeItem('organization_id');
  };

  // Set prefs and remove them from query params
  useEffect(() => {
    if (queryInviteId) {
      setPref('inviteId', queryInviteId);
    }

    if (queryBundleId) {
      setPref('bundle', { bundleId: queryBundleId });
    }

    if (queryTempCompanyId) {
      setPref('tempCompanyId', queryTempCompanyId);
    }

    setQuery({ inviteId: null, bundleId: null, tempCompanyId: null });
    setHasProcessedInvite(true);
  }, [queryInviteId, queryBundleId, queryTempCompanyId]);

  // TODO: Right now, upon login our page reloads twice, this is necessary to ensure that the bundle is processed after reload
  useEffect(() => {
    if (bundle?.bundleId && isLoggedIn && !isProcessingCallback) {
      setTimeout(() => setQuery({ loadBundle: true }), 3000);
    }
  }, [bundle?.bundleId, isProcessingCallback, isLoggedIn]);

  // Process post signup
  useEffect(() => {
    const processSignUp = async () => {
      // Only run if:
      //   - we're in the home page and we've completed the signup
      //   - the user is logged in, so it has an id
      //   - this code hasn't run yet
      //   - we've processed the invite query params into redux
      //   - the user has at least the personal company
      const shouldRun = window.location.pathname === '/' && isSignUpComplete && isLoggedIn && user.id && !hasRan && hasProcessedInvite && userCompanies?.length > 0;
      if (shouldRun) {
        setHasRan(true);
        action('CLEAR_SIGNUP_STATE');
        gtag?.('event', 'conversion', { send_to: 'AW-10897846390/o04NCMqP9b4aEPbgv8wo' }); // eslint-disable-line

        const organizationName = localStorage.getItem('organization_name');
        const organizationId = localStorage.getItem('organization_id');
        const allowDiscovery = localStorage.getItem('allow_discovery');
        const organizationDomain = localStorage.getItem('organization_domain');
        const solution = localStorage.getItem('achievement');
        let companyId;
        let url;

        if (organizationName) {
          const settings = {};
          if (solution) {
            settings.solution = solution;
          }

          if (allowDiscovery) {
            settings.allowSameDomainDiscovery = true;
            if (organizationDomain) {
              settings.companyDomains = [organizationDomain];
            }
          }

          const response = await postCompany({ name: organizationName, ownerId: user.id, settings });
          companyId = response?.activeCompanyId;
        }

        if (organizationId) {
          await joinCompanyFromDiscovery({ companyId: organizationId });
          companyId = organizationId;
        }

        if (!companyId) {
          companyId = userCompanies?.find((c) => c?.isPersonal)?.id || userCompanies?.[0]?.id;
        }

        if (inviteId) {
          const inviteResponse = await processInvite({ inviteId });

          if (inviteResponse?.companyId) {
            companyId = inviteResponse.companyId;

            if (inviteResponse?.hasBundle) {
              setPref('bundle', { inviteId, companyId });
            }

            if (inviteResponse?.url) {
              url = inviteResponse.url;
            } else if (inviteResponse?.hasBundle) {
              setQuery({ loadBundle: true });
            }
          } else {
            sendToast({
              message: 'This invite could not be processed. You may have signed up with a different email than the one invited.',
              type: 'error',
              duration: 5000
            });
          }

          removePref('inviteId');
        }

        changeCompany(companyId, { message: MESSAGE, isSignUp: true, redirect: true }, () => {
          if (url) {
            finishSignUp();
            window.open(url, '_self');
          } else {
            getUserState({ userId: user?.id }, finishSignUp);
          }
        });
      }
    };

    processSignUp();
  }, [isSignUpComplete, isLoggedIn, user?.id, hasRan, hasProcessedInvite, inviteId, userCompanies]);

  // Process bundle
  useEffect(() => {
    const shouldProcessBundleFromInvite = queryLoadBundle && bundle?.inviteId && bundle?.companyId === companyId && !isLoadingBundle;
    const shouldProcessBundleFromBundleId = bundle?.bundleId && !isLoadingBundle && isLoggedIn && companyId && companyId !== tempCompanyId && queryLoadBundle;

    if (shouldProcessBundleFromInvite || shouldProcessBundleFromBundleId) {
      action('SET_TRANSITION_SIGN_UP', true);

      processBundle({ inviteId: bundle?.inviteId, bundleId: bundle?.bundleId, companyId: bundle?.companyId || companyId, tempCompanyId }, () => {
        getOrgState();

        removePref('bundle');
        removePref('tempCompanyId');
        setQuery({ loadBundle: null });
        action('SET_TRANSITION_SIGN_UP', false);
      });
    }
  }, [queryLoadBundle, bundle, isLoadingBundle, companyId, isLoggedIn, tempCompanyId, isProcessingCallback]);

  return null;
};

export default PostSignUpHandler;
